import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner, FaClock } from 'react-icons/fa6';

interface PreparingStateProps {
  elapsedSeconds: number;
}

/**
 * PreparingState sub-component
 * Shows spinner and preparing message while waiting for training data
 */
export const PreparingState: React.FC<PreparingStateProps> = memo(({ elapsedSeconds }) => {
  const { t } = useTranslation('training');

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden text-center">
      <div className="px-6 py-8 sm:px-10 sm:py-12">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-accent mb-5 sm:mb-6">
          <FaSpinner className="text-primary text-lg sm:text-xl animate-spin" />
        </div>
        <h2 className="text-sm sm:text-base font-semibold text-foreground mb-1.5 tracking-tight">
          {t('training.overlay.preparingData', { defaultValue: 'Daten werden für das Training vorbereitet...' })}
        </h2>
        <p className="text-[11px] sm:text-xs text-muted-foreground leading-relaxed max-w-sm mx-auto">
          {t('training.overlay.pleaseWait', { defaultValue: 'Bitte warten Sie, während das System die Daten verarbeitet.' })}
        </p>
        {elapsedSeconds > 5 && (
          <div className="inline-flex items-center gap-1.5 mt-4 sm:mt-5 px-3 py-1.5 bg-muted/80 rounded-lg border border-border">
            <FaClock className="text-muted-foreground text-[10px]" />
            <span className="text-[10px] sm:text-[11px] text-muted-foreground font-mono tabular-nums">
              {t('training.overlay.elapsedTime', { defaultValue: '{{seconds}}s vergangen...', seconds: elapsedSeconds })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

PreparingState.displayName = 'PreparingState';

export default PreparingState;
